import React from 'react';
import { Loader2, ShieldCheck, ShieldOff, Radio, Camera, Cloud, HelpCircle, DoorOpen } from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
  VehicleAccessZone,
  VehicleAccessDeviceKind,
  accessDeviceKindLabel,
  vehicleAccessSummary,
  vehicleReadableZones,
  VehicleAccessSourceInfo,
} from '../../../utils/vehicleAccess';

const KIND_ICON: Record<VehicleAccessDeviceKind, React.ComponentType<{ className?: string }>> = {
  rfid: Radio,
  guarita: DoorOpen,
  lpr: Camera,
  lpr_cloud: Cloud,
  other: HelpCircle,
};

/**
 * Permissões — where this vehicle may go, and through which hardware.
 *
 * Reads as an answer to "why doesn't this car open the garage?", which is the question the portaria
 * actually asks. Three things it says that nothing in the app said before:
 *
 *  - whose access this is (a vehicle has none of its own — it inherits from its owner or its unit);
 *  - which devices in each zone can identify a CAR, as opposed to merely being in the zone;
 *  - which of those a sync writes to, since one camera type grants access without ever being synced.
 */
export const VehicleAccessTab: React.FC<{
  source: VehicleAccessSourceInfo;
  zones: VehicleAccessZone[];
  loading: boolean;
  error: string | null;
  lightTheme: boolean;
}> = ({ source, zones, loading, error, lightTheme }) => {
  const summary = vehicleAccessSummary(zones);
  // Only what can identify a CAR. Listing every zone with its facial readers marked "não identifica
  // veículo" meant nine cards around the single antenna that mattered — the answer was in there,
  // buried. The remainder is one line below, not eight cards.
  const visible = vehicleReadableZones(zones);

  const card = cn('rounded-xl border p-3', lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900/50 border-gray-800');
  const label = cn('text-[10px] font-bold uppercase tracking-widest', lightTheme ? 'text-gray-400' : 'text-gray-600');
  const muted = cn('text-xs', lightTheme ? 'text-gray-500' : 'text-gray-500');

  return (
    <div className="space-y-3">
      {/* Whose access this is. First, because every other number on the page depends on it. */}
      <div className={card}>
        <p className={label}>Origem do acesso</p>
        <p className={cn('text-sm mt-1', lightTheme ? 'text-gray-800' : 'text-gray-200')}>{source.label}</p>
        <p className={cn(muted, 'mt-1')}>
          Um veículo não tem permissões próprias: ele entra onde o responsável pode entrar.
        </p>
      </div>

      {source.kind === 'none' ? (
        // Not an error and not an empty list — there is nobody to inherit from, which is also why
        // sincronizar would do nothing. Saying so is the difference between "quebrado" and "nunca
        // foi configurado".
        <div className={card}>
          <p className={cn('text-sm', lightTheme ? 'text-gray-700' : 'text-gray-300')}>
            Este veículo não está vinculado a um morador nem a uma unidade, portanto não possui acesso
            a nenhuma zona. Vincule-o na edição para que ele herde as permissões.
          </p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className={cn('h-5 w-5 animate-spin', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
        </div>
      ) : error ? (
        // Separated from "no zones": a failed lookup shown as an empty list reads as "this car has
        // no access", which is a very different thing to act on.
        <div className={card}>
          <p className={cn('text-sm', lightTheme ? 'text-red-600' : 'text-red-400')}>{error}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat value={visible.filter(z => !z.denied).length} caption="Zonas" lightTheme={lightTheme} />
            <Stat value={summary.readers} caption="Leitores" lightTheme={lightTheme} />
            <Stat value={summary.deniedZones} caption="Bloqueios" lightTheme={lightTheme} alert={summary.deniedZones > 0} />
          </div>

          {summary.allowedZones > 0 && summary.readers === 0 && (
            // THE answer to "why doesn't the gate open": the zones are there, but nothing in them
            // can read a tag or a plate.
            <div className={cn('rounded-xl border p-3', lightTheme ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/10 border-amber-500/30')}>
              <p className={cn('text-xs', lightTheme ? 'text-amber-800' : 'text-amber-300')}>
                As zonas liberadas não possuem leitor RFID, guarita ou câmera LPR — nenhum
                dispositivo consegue identificar um veículo nelas.
              </p>
            </div>
          )}

          {zones.length === 0 ? (
            <div className={card}>
              <p className={cn('text-sm', lightTheme ? 'text-gray-700' : 'text-gray-300')}>
                O responsável não tem nenhuma zona de acesso atribuída.
              </p>
            </div>
          ) : (
            visible.map((zone) => (
              <div key={zone.id} className={cn('rounded-xl border overflow-hidden', lightTheme ? 'border-gray-200' : 'border-gray-800')}>
                <div className={cn('px-3 py-2 border-b flex items-center gap-2', lightTheme ? 'border-gray-200 bg-gray-50' : 'border-gray-800 bg-gray-900/50')}>
                  {zone.denied
                    ? <ShieldOff className={cn('h-3.5 w-3.5 flex-shrink-0', lightTheme ? 'text-red-500' : 'text-red-400')} />
                    : <ShieldCheck className={cn('h-3.5 w-3.5 flex-shrink-0', lightTheme ? 'text-green-600' : 'text-green-400')} />}
                  <span className={cn('text-sm font-semibold truncate', lightTheme ? 'text-gray-800' : 'text-gray-200')}>{zone.name}</span>
                  {zone.denied && (
                    <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0', lightTheme ? 'bg-red-100 text-red-600' : 'bg-red-500/15 text-red-400')}>
                      BLOQUEADO
                    </span>
                  )}
                </div>

                {zone.devices.length === 0 ? (
                  <p className={cn('px-3 py-2.5', muted)}>Nenhum dispositivo nesta zona.</p>
                ) : (
                  zone.devices.map((d) => {
                    const Icon = KIND_ICON[d.kind];
                    return (
                      <div key={d.id} className={cn('flex items-center gap-2.5 px-3 py-2 border-b last:border-0', lightTheme ? 'border-gray-100' : 'border-gray-800/60')}>
                        <Icon className={cn('h-3.5 w-3.5 flex-shrink-0',
                          !d.readsVehicle || zone.denied ? (lightTheme ? 'text-gray-300' : 'text-gray-700')
                            : (lightTheme ? 'text-blue-600' : 'text-blue-400'))} />
                        <div className="min-w-0 flex-1">
                          <p className={cn('text-xs font-medium truncate',
                            !d.readsVehicle || zone.denied
                              ? (lightTheme ? 'text-gray-400' : 'text-gray-600')
                              : (lightTheme ? 'text-gray-800' : 'text-gray-200'))}>
                            {d.name}
                          </p>
                          <p className={cn('text-[11px] truncate', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
                            {accessDeviceKindLabel(d.kind)}
                            {d.readsVehicle && !d.syncTarget && ' · reconhece pela nuvem, não precisa sincronizar'}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ))
          )}

          {summary.zonesWithoutReader > 0 && (
            // Counted, never listed: these are pedestrian gates and facial-only doors the
            // responsible person can use and the car cannot. Saying so in a line keeps the screen
            // honest without burying the readers that matter.
            <p className={cn('text-[11px] px-1', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
              {summary.zonesWithoutReader === 1
                ? '1 outra zona do responsável não possui leitor de veículo (facial, pedestres).'
                : `${summary.zonesWithoutReader} outras zonas do responsável não possuem leitor de veículo (facial, pedestres).`}
            </p>
          )}
        </>
      )}
    </div>
  );
};

const Stat: React.FC<{ value: number; caption: string; lightTheme: boolean; alert?: boolean }> = ({
  value, caption, lightTheme, alert,
}) => (
  <div className={cn('rounded-xl border px-3 py-2 text-center', lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900/50 border-gray-800')}>
    <p className={cn('text-lg font-bold leading-none',
      alert ? (lightTheme ? 'text-red-600' : 'text-red-400') : (lightTheme ? 'text-gray-900' : 'text-white'))}>
      {value}
    </p>
    <p className={cn('text-[10px] uppercase tracking-wider mt-1', lightTheme ? 'text-gray-400' : 'text-gray-600')}>{caption}</p>
  </div>
);
